import { __assign } from "tslib";
import { useCallback, useEffect, useMemo, useRef, } from 'react';
import computeScrollIntoView from 'compute-scroll-into-view';
import { useListNavigationKeys, } from '../useListNavigationKeys/useListNavigationKeys';
/** Hook that lets you make a list where you can scroll through
 *  with keyboard and mouse, calling back the (to be) highlighted
 *  and selected item.
 *
 *  It does NOT render anything, it's up to you to render the items itself. */
export function useScrollableList(_a) {
    var highlightedItem = _a.highlightedItem, selectedItem = _a.selectedItem, items = _a.items, onHighlight = _a.onHighlight, onSelect = _a.onSelect, onEscape = _a.onEscape, scrollContainerRef = _a.scrollContainerRef, _b = _a.keyEventTarget, keyEventTarget = _b === void 0 ? document : _b;
    var itemRefs = useRef({});
    itemRefs.current = {};
    var selectedIndex = selectedItem !== undefined ? items.indexOf(selectedItem) : -1;
    var highlightedIndex = highlightedItem !== undefined
        ? items.indexOf(highlightedItem)
        : selectedIndex;
    var prevHighlightedItemForScrollingRef = useRef(highlightedItem);
    /* Refs used to prevent scrolling to highlighted item on mouse move  */
    var shouldScroll = useRef(true);
    var prevMousePos = useRef({ x: 0, y: 0 });
    var mouseMove = useCallback(function (e, index) {
        var mousePos = { x: e.screenX, y: e.screenY };
        if (mousePos.x === prevMousePos.current.x &&
            mousePos.y === prevMousePos.current.y) {
            return;
        }
        shouldScroll.current = false;
        prevMousePos.current = mousePos;
        var newHighlightedItem = items[index];
        if (highlightedItem !== newHighlightedItem) {
            onHighlight(newHighlightedItem);
        }
    }, [highlightedItem, onHighlight, items]);
    var getItemProps = useCallback(function (_a) {
        var index = _a.index, itemProps = _a.itemProps;
        return __assign(__assign({}, (itemProps || {})), { ref: function (ref) {
                itemRefs.current[index] = ref;
            }, onMouseMove: function (e) {
                var _a;
                mouseMove(e, index);
                (_a = itemProps === null || itemProps === void 0 ? void 0 : itemProps.onMouseMove) === null || _a === void 0 ? void 0 : _a.call(itemProps, e);
            }, onClick: function (e) {
                var _a, _b;
                onSelect((_a = items[index]) !== null && _a !== void 0 ? _a : undefined);
                (_b = itemProps === null || itemProps === void 0 ? void 0 : itemProps.onClick) === null || _b === void 0 ? void 0 : _b.call(itemProps, e);
            } });
    }, [mouseMove, items, onSelect]);
    var getItemElement = useCallback(function (item) {
        if (item === undefined) {
            return;
        }
        var idx = items.indexOf(item);
        var itemRef = itemRefs.current[idx];
        if (!itemRef) {
            // eslint-disable-next-line no-console
            console.error('No reference found for an item, are you using a Component without forwardRef?');
            return;
        }
        return itemRef;
    }, [items]);
    /* Highlight a number if defined and if change does not come from mouse (hence, keyboard) */
    useEffect(function () {
        var itemElement = getItemElement(highlightedItem);
        if (!shouldScroll.current ||
            !itemElement ||
            prevHighlightedItemForScrollingRef.current === highlightedItem) {
            shouldScroll.current = true;
            return;
        }
        shouldScroll.current = true;
        var actions = computeScrollIntoView(itemElement, {
            boundary: scrollContainerRef.current,
            block: 'nearest',
            scrollMode: 'if-needed',
        });
        actions.forEach(function (_a) {
            var el = _a.el, top = _a.top;
            el.scrollTop = top;
        });
        prevHighlightedItemForScrollingRef.current = highlightedItem;
    }, [highlightedItem, getItemElement]);
    /* Initially scroll to already selected (is also highlighted) item */
    useEffect(function () {
        var itemElement = getItemElement(selectedItem);
        if (!itemElement) {
            return;
        }
        scrollContainerRef.current.scrollTop = itemElement.offsetTop;
    }, [
    /* no deps, only initial */
    ]);
    var keyNavKeysArgs = useMemo(function () {
        return {
            onUp: function () {
                var _a;
                onHighlight((_a = items[highlightedIndex - 1]) !== null && _a !== void 0 ? _a : items[items.length - 1]);
            },
            onDown: function () {
                var _a;
                onHighlight((_a = items[highlightedIndex + 1]) !== null && _a !== void 0 ? _a : items[0]);
            },
            onHome: function () {
                onHighlight(items[0]);
            },
            onEnd: function () {
                onHighlight(items[items.length - 1]);
            },
            onEnter: function () {
                var _a;
                onSelect((_a = items[highlightedIndex]) !== null && _a !== void 0 ? _a : undefined);
            },
            onEscape: function () {
                onEscape === null || onEscape === void 0 ? void 0 : onEscape();
            },
            target: keyEventTarget,
        };
    }, [highlightedIndex, items, onHighlight, onSelect, onEscape, keyEventTarget]);
    useListNavigationKeys(keyNavKeysArgs);
    return {
        getItemProps: getItemProps,
    };
}
//# sourceMappingURL=useScrollableList.js.map